// Trybe creator outreach — pick a brand, preview every creator who posted for
// it, press Run. The Brand-Dashboard worker cron (every 10 min) then sends the
// fixed 3-email sequence (today / day 3 / day 7) through cr_email_queue.
//
// Data: outreach_list_brands() + outreach_brand_creators(p_brand) RPCs and the
// creator_outreach_runs / creator_outreach_enrollments tables (creator-app
// migration 20260910120000_creator_outreach.sql). Status view:
// creator_outreach_enrollment_status.
//
// Spec + handoff: Brand-Dashboard/docs/trybe-creator-outreach.md

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { Search, Send, Users, StopCircle, RefreshCw, ExternalLink, Link2, Pencil, AlertTriangle } from 'lucide-react';
import { logCreate, logUpdate } from '@/lib/audit-log';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { getOutreachJoinUrl, workerAuthedFetch } from '@/lib/workerApi';

/** Matches the DB CHECK on creator_outreach_runs.trybe_join_url and the
 *  worker's safeJoinUrlParam, so a link the dialog accepts can never be the
 *  thing that fails the insert or gets dropped by the preview. Quotes and
 *  angle brackets are out because this value ends up inside href="..." in the
 *  email the creator receives. */
const JOIN_URL_RE = /^https:\/\/[^\s"'<>]+$/;

type BrandRow = { brand_name: string; source: 'hybrid' | 'roster'; creators: number; videos: number };
type BrandGroup = { brand_name: string; sources: string[]; creators: number; videos: number };
type CreatorRow = {
  roster_creator_id: string | null;
  email: string;
  first_name: string | null;
  handle: string | null;
  source: string;
  /** Videos that will actually go in the email — approved plus unreviewed.
   *  Rejected ones are excluded: the brand already turned those down, and the
   *  whole pitch is that they want to run them as ads. */
  video_urls: string[];
  video_count: number;
  approved_count: number;
  pending_count: number;
  /** Excluded from video_count. Shown so the number can be reconciled against
   *  what the creator thinks they submitted. */
  rejected_count: number;
  unsubscribed: boolean;
};
type RunRow = {
  id: string;
  brand_name: string;
  template: string;
  status: string;
  created_by: string | null;
  recipient_count: number | null;
  error: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
};
type EnrollmentStatusRow = {
  id: string;
  run_id: string;
  email: string;
  handle: string | null;
  source: string;
  video_count: number;
  status: string;
  stop_reason: string | null;
  step_1_sent_at: string | null;
  step_2_sent_at: string | null;
  step_3_sent_at: string | null;
  step_1_queue_status: string | null;
  step_2_queue_status: string | null;
  step_3_queue_status: string | null;
  step_1_opened: boolean;
  step_2_opened: boolean;
  step_3_opened: boolean;
  bounced: boolean;
  trybe_joined_at: string | null;
  trybe_assigned_at: string | null;
  last_error: string | null;
  // Per-step send detail off cr_email_queue — these emails are filtered out of
  // the job operations inspector, so this page is where they get explained.
  step_1_queue_error: string | null;
  step_2_queue_error: string | null;
  step_3_queue_error: string | null;
  step_1_attempts: number | null;
  step_2_attempts: number | null;
  step_3_attempts: number | null;
};

/** A row an admin probably has to do something about. */
function needsAttention(r: EnrollmentStatusRow): boolean {
  return r.bounced
    || !!r.last_error
    || r.status === 'failed'
    || [r.step_1_queue_status, r.step_2_queue_status, r.step_3_queue_status].includes('failed');
}

type RunFilter = 'all' | 'attention' | 'unsubscribed' | 'joined';

const RUN_FILTERS: { key: RunFilter; label: string }[] = [
  { key: 'all',          label: 'All' },
  { key: 'attention',    label: 'Needs attention' },
  { key: 'unsubscribed', label: 'Unsubscribed' },
  { key: 'joined',       label: 'Joined Trybe' },
];

const SEQUENCE_SUMMARY = 'Sends the Trybe invite sequence: email 1 today, email 2 on day 3, email 3 on day 7. Creators who unsubscribe drop out automatically.';

const runStatusVariant: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  queued: 'outline',
  active: 'default',
  completed: 'secondary',
  cancelled: 'secondary',
  failed: 'destructive',
};

function parseManualEntries(text: string): { email: string; handle: string | null }[] {
  // One per line or comma separated. "email" or "email, handle" or "@handle email".
  const out: { email: string; handle: string | null }[] = [];
  for (const raw of text.split(/[\n,;]+/)) {
    const parts = raw.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) continue;
    const email = parts.find(p => p.includes('@') && !p.startsWith('@'));
    if (!email) continue;
    const handle = parts.find(p => p.startsWith('@'))?.replace(/^@/, '') ?? null;
    out.push({ email: email.toLowerCase(), handle });
  }
  return out;
}

async function currentAdminEmail(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data?.user?.email ?? null;
}

export default function TrybeOutreach() {
  const queryClient = useQueryClient();
  const [brandSearch, setBrandSearch] = useState('');
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [emailEdits, setEmailEdits] = useState<Record<string, string>>({});
  const [manualText, setManualText] = useState('');
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [runFilter, setRunFilter] = useState<RunFilter>('all');

  // ── Trybe link confirmation ────────────────────────────────────────────
  // The link every email in a run points at used to be invisible here: it was
  // resolved inside the worker at SEND time, so nobody starting a run could
  // see where they were sending people until the mail had already gone. The
  // run is now confirmed against a link the admin has actually read, and that
  // link is stored on the run (creator_outreach_runs.trybe_join_url) so all
  // three emails carry it however long the sequence takes to drain.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(false);
  const [linkDraft, setLinkDraft] = useState('');

  // The worker is the only place the default lives — see getOutreachJoinUrl.
  // Fetched when the dialog opens rather than on mount: it is one request per
  // run start, not per page view.
  const joinUrlQuery = useQuery({
    queryKey: ['outreach-join-url'],
    queryFn: getOutreachJoinUrl,
    enabled: confirmOpen,
    staleTime: 5 * 60_000,
  });

  const effectiveLink = (linkDraft || joinUrlQuery.data?.url || '').trim();
  const linkValid = JOIN_URL_RE.test(effectiveLink);

  // ── Brand list ─────────────────────────────────────────────────────────
  const brandsQuery = useQuery({
    queryKey: ['outreach-brands'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('outreach_list_brands');
      if (error) throw error;
      const groups = new Map<string, BrandGroup>();
      for (const r of (data ?? []) as BrandRow[]) {
        const key = r.brand_name.trim().toLowerCase();
        const g = groups.get(key) ?? { brand_name: r.brand_name, sources: [], creators: 0, videos: 0 };
        g.sources.push(r.source);
        g.creators += Number(r.creators);
        g.videos += Number(r.videos);
        groups.set(key, g);
      }
      return [...groups.values()].sort((a, b) => b.creators - a.creators);
    },
  });

  const filteredBrands = useMemo(() => {
    const q = brandSearch.trim().toLowerCase();
    const all = brandsQuery.data ?? [];
    return q ? all.filter(b => b.brand_name.toLowerCase().includes(q)) : all;
  }, [brandsQuery.data, brandSearch]);

  // ── Creators for the selected brand ─────────────────────────────────────
  const creatorsQuery = useQuery({
    queryKey: ['outreach-brand-creators', selectedBrand],
    enabled: !!selectedBrand,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('outreach_brand_creators', { p_brand: selectedBrand });
      if (error) throw error;
      return (data ?? []) as CreatorRow[];
    },
  });

  // Emails already enrolled in a previous run for this brand (any status).
  // Two different facts, and only one of them is a blocker.
  //
  //   inFlight  an ACTIVE enrollment on a run that is still queued or active.
  //             Enrolling them again means two overlapping sequences for one
  //             brand — six near-identical emails — so they cannot be picked.
  //             The database refuses it too
  //             (creator_outreach_block_concurrent_enrollment, 20260923000003);
  //             filtering here is what stops that raise losing the whole batch.
  //
  //   everSent  was in ANY run for this brand, finished or not. Re-running a
  //             brand later is a legitimate campaign, so this only labels the
  //             row and leaves the call to the admin.
  //
  // ilike, not eq: the trigger and the RPC both match the brand name
  // case-insensitively, and this has to agree with them or the page offers
  // someone the insert will then reject.
  const priorQuery = useQuery({
    queryKey: ['outreach-prior', selectedBrand],
    enabled: !!selectedBrand,
    queryFn: async () => {
      const empty = { inFlight: new Set<string>(), everSent: new Set<string>() };
      const { data: runs, error } = await supabase
        .from('creator_outreach_runs')
        .select('id, status')
        .ilike('brand_name', selectedBrand ?? '');
      if (error) throw error;
      const all = (runs ?? []) as { id: string; status: string }[];
      if (!all.length) return empty;
      const liveRunIds = new Set(all.filter(r => r.status === 'queued' || r.status === 'active').map(r => r.id));
      const { data: enr, error: e2 } = await supabase
        .from('creator_outreach_enrollments')
        .select('email, status, run_id')
        .in('run_id', all.map(r => r.id));
      if (e2) throw e2;
      const inFlight = new Set<string>();
      const everSent = new Set<string>();
      for (const r of (enr ?? []) as { email: string; status: string; run_id: string }[]) {
        const email = r.email.trim().toLowerCase();
        everSent.add(email);
        if (r.status === 'active' && liveRunIds.has(r.run_id)) inFlight.add(email);
      }
      return { inFlight, everSent };
    },
  });

  // ── Runs + status ──────────────────────────────────────────────────────
  const runsQuery = useQuery({
    queryKey: ['outreach-runs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('creator_outreach_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as RunRow[];
    },
    refetchInterval: 60000,
  });

  // "Joined Trybe" count per run for the runs table.
  const joinedCounts = useQuery({
    queryKey: ['outreach-joined-counts', runsQuery.data?.map(r => r.id).join(',')],
    enabled: !!runsQuery.data?.length,
    queryFn: async () => {
      const ids = (runsQuery.data ?? []).map(r => r.id);
      const { data, error } = await supabase
        .from('creator_outreach_enrollments')
        .select('run_id')
        .in('run_id', ids)
        .not('trybe_joined_at', 'is', null);
      if (error) throw error;
      const out: Record<string, number> = {};
      for (const row of (data ?? []) as { run_id: string }[]) out[row.run_id] = (out[row.run_id] ?? 0) + 1;
      return out;
    },
    refetchInterval: 60000,
  });

  const runStatusQuery = useQuery({
    queryKey: ['outreach-run-status', openRunId],
    enabled: !!openRunId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('creator_outreach_enrollment_status')
        .select('*')
        .eq('run_id', openRunId)
        .order('handle', { ascending: true });
      if (error) throw error;
      return (data ?? []) as EnrollmentStatusRow[];
    },
    refetchInterval: 30000,
  });

  // ── Recipients = previewed creators − excluded + manual adds ───────────
  const recipients = useMemo(() => {
    const seen = new Set<string>();
    const rows: { email: string; handle: string | null; first_name: string | null; roster_creator_id: string | null; source: string; video_urls: string[]; video_count: number }[] = [];
    for (const c of creatorsQuery.data ?? []) {
      if (c.unsubscribed || excluded.has(c.email)) continue;
      const email = (emailEdits[c.email] ?? c.email).trim().toLowerCase();
      if (!email.includes('@') || seen.has(email)) continue;
      // Already part way through a sequence for this brand — see priorQuery.
      if (priorQuery.data?.inFlight.has(email)) continue;
      seen.add(email);
      rows.push({ email, handle: c.handle, first_name: c.first_name, roster_creator_id: c.roster_creator_id, source: c.source, video_urls: c.video_urls ?? [], video_count: c.video_count ?? 0 });
    }
    // A manually pasted address may belong to a creator the preview already
    // resolved — most often one the admin excluded and then re-added by hand.
    // Match on address so the enrollment inherits their roster id, which is
    // what gives the email a real unsubscribe link instead of a mailto:, and
    // what lets the send-time opt-out check find them at all.
    const known = new Map(
      (creatorsQuery.data ?? []).map(c => [c.email.trim().toLowerCase(), c] as const),
    );
    for (const m of parseManualEntries(manualText)) {
      if (seen.has(m.email)) continue;
      if (priorQuery.data?.inFlight.has(m.email)) continue;
      const match = known.get(m.email);
      // Never let a manual add put an unsubscribed creator back into a run.
      // The worker re-checks by address too, so an address the preview never
      // saw is still caught there; this just keeps it out of the count the
      // admin is looking at before they press Run.
      if (match?.unsubscribed) continue;
      seen.add(m.email);
      rows.push({
        email: m.email,
        handle: m.handle ?? match?.handle ?? null,
        first_name: match?.first_name ?? m.handle,
        roster_creator_id: match?.roster_creator_id ?? null,
        source: 'manual',
        video_urls: [],
        video_count: 0,
      });
    }
    return rows;
  }, [creatorsQuery.data, excluded, emailEdits, manualText, priorQuery.data]);

  // Counted over the people who will actually be emailed, not the whole
  // preview — unticking someone should take their pending videos off the
  // to-review total too.
  const videoTotals = useMemo(() => {
    const included = new Set(recipients.map(r => r.email));
    const rows = (creatorsQuery.data ?? []).filter(
      c => included.has((emailEdits[c.email] ?? c.email).trim().toLowerCase()),
    );
    return {
      inEmail:  rows.reduce((n, c) => n + (c.video_count ?? 0), 0),
      approved: rows.reduce((n, c) => n + (c.approved_count ?? 0), 0),
      pending:  rows.reduce((n, c) => n + (c.pending_count ?? 0), 0),
      rejected: rows.reduce((n, c) => n + (c.rejected_count ?? 0), 0),
      creatorsWithPending: rows.filter(c => (c.pending_count ?? 0) > 0).length,
    };
  }, [creatorsQuery.data, recipients, emailEdits]);

  const alreadySentCount = useMemo(
    () => recipients.filter(r => priorQuery.data?.everSent.has(r.email)).length,
    [recipients, priorQuery.data],
  );

  const inFlightCount = useMemo(
    () => (creatorsQuery.data ?? []).filter(
      c => priorQuery.data?.inFlight.has((emailEdits[c.email] ?? c.email).trim().toLowerCase()),
    ).length,
    [creatorsQuery.data, emailEdits, priorQuery.data],
  );

  // These emails are deliberately absent from the job operations inspector, so
  // the run has to account for itself here: where every creator got to, and how
  // many individual sends landed, failed or bounced.
  const runSummary = useMemo(() => {
    const rows = runStatusQuery.data ?? [];
    const steps = rows.flatMap(r => [
      { s: r.step_1_queue_status, sent: r.step_1_sent_at },
      { s: r.step_2_queue_status, sent: r.step_2_sent_at },
      { s: r.step_3_queue_status, sent: r.step_3_sent_at },
    ].filter(x => x.sent));
    return {
      creators:     rows.length,
      active:       rows.filter(r => r.status === 'active').length,
      done:         rows.filter(r => r.status === 'done').length,
      stopped:      rows.filter(r => r.status === 'stopped').length,
      unsubscribed: rows.filter(r => r.status === 'unsubscribed').length,
      attention:    rows.filter(needsAttention).length,
      opened:       rows.filter(r => r.step_1_opened || r.step_2_opened || r.step_3_opened).length,
      bounced:      rows.filter(r => r.bounced).length,
      joined:       rows.filter(r => r.trybe_joined_at || r.trybe_assigned_at).length,
      emailsSent:   steps.filter(x => x.s === 'sent').length,
      emailsFailed: steps.filter(x => x.s === 'failed').length,
      emailsQueued: steps.filter(x => x.s === 'queued' || x.s === 'sending' || x.s == null).length,
    };
  }, [runStatusQuery.data]);

  const visibleRunRows = useMemo(() => {
    const rows = runStatusQuery.data ?? [];
    if (runFilter === 'attention')    return rows.filter(needsAttention);
    if (runFilter === 'unsubscribed') return rows.filter(r => r.status === 'unsubscribed');
    if (runFilter === 'joined')       return rows.filter(r => r.trybe_joined_at || r.trybe_assigned_at);
    return rows;
  }, [runStatusQuery.data, runFilter]);

  // ── Run ────────────────────────────────────────────────────────────────
  const runMutation = useMutation({
    mutationFn: async (joinUrl: string) => {
      if (!selectedBrand) throw new Error('Pick a brand first');
      if (!recipients.length) throw new Error('No recipients');
      // Re-checked here, not just in the dialog: this is the value that goes
      // to every recipient, and the DB CHECK is the only other thing standing
      // between a malformed link and 300 inboxes.
      if (!JOIN_URL_RE.test(joinUrl)) throw new Error('The Trybe link must be a full https:// URL');
      const createdBy = await currentAdminEmail();
      const { data: run, error } = await supabase
        .from('creator_outreach_runs')
        .insert({
          brand_name: selectedBrand,
          template: 'trybe',
          sources: ['hybrid', 'roster', ...(manualText.trim() ? ['manual'] : [])],
          status: 'queued',
          created_by: createdBy,
          recipient_count: recipients.length,
          // Pinned per run — the worker reads this instead of resolving the
          // link at send time, so changing the deployed default never
          // re-points a sequence that is already part way through.
          trybe_join_url: joinUrl,
        })
        .select('id')
        .single();
      if (error) throw error;
      const runId = (run as { id: string }).id;
      const { error: e2 } = await supabase.from('creator_outreach_enrollments').insert(
        recipients.map(r => ({
          run_id: runId,
          roster_creator_id: r.roster_creator_id,
          email: r.email,
          first_name: r.first_name,
          handle: r.handle,
          source: r.source,
          video_urls: r.video_urls,
          video_count: r.video_count,
          status: 'active',
        })),
      );
      if (e2) {
        await supabase.from('creator_outreach_runs').update({ status: 'failed', error: e2.message }).eq('id', runId);
        throw e2;
      }
      await logCreate('creator_outreach_runs', runId, {
        brand_name: selectedBrand,
        recipient_count: recipients.length,
        template: 'trybe',
        // On the audit row so "which link did that run actually send?" is
        // answerable later without reading the run back.
        trybe_join_url: joinUrl,
      });
      return runId;
    },
    onSuccess: (runId) => {
      toast.success(`Run started for ${selectedBrand}. ${recipients.length} creators will get email 1 within about 10 minutes.`);
      setExcluded(new Set());
      setEmailEdits({});
      setManualText('');
      setConfirmOpen(false);
      setEditingLink(false);
      setLinkDraft('');
      setOpenRunId(runId);
      queryClient.invalidateQueries({ queryKey: ['outreach-runs'] });
      queryClient.invalidateQueries({ queryKey: ['outreach-prior'] });
    },
    onError: (e: Error) => toast.error(`Could not start run: ${e.message}`),
  });

  const stopEnrollment = useMutation({
    mutationFn: async (row: EnrollmentStatusRow) => {
      const { error } = await supabase
        .from('creator_outreach_enrollments')
        .update({ status: 'stopped', stop_reason: 'stopped by admin' })
        .eq('id', row.id);
      if (error) throw error;
      await logUpdate('creator_outreach_enrollments', row.id, { status: row.status }, { status: 'stopped' });
    },
    onSuccess: () => {
      toast.success('Stopped. No further emails to this creator for this run.');
      queryClient.invalidateQueries({ queryKey: ['outreach-run-status', openRunId] });
      // They are no longer mid-sequence, so the preview may offer them again.
      queryClient.invalidateQueries({ queryKey: ['outreach-prior'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Trybe has no per-brand invite link, so "joined our program" and "assigned
  // the brand" are things Nick does by hand inside Trybe and records here.
  const markTrybe = useMutation({
    mutationFn: async ({ row, field }: { row: EnrollmentStatusRow; field: 'trybe_joined_at' | 'trybe_assigned_at' }) => {
      const now = new Date().toISOString();
      const patch: Record<string, string> = { [field]: now };
      if (field === 'trybe_assigned_at' && !row.trybe_joined_at) patch.trybe_joined_at = now;
      const { error } = await supabase.from('creator_outreach_enrollments').update(patch).eq('id', row.id);
      if (error) throw error;
      await logUpdate('creator_outreach_enrollments', row.id, { [field]: null }, patch);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['outreach-run-status', openRunId] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const cancelRun = useMutation({
    mutationFn: async (run: RunRow) => {
      const { error } = await supabase
        .from('creator_outreach_enrollments')
        .update({ status: 'stopped', stop_reason: 'run cancelled by admin' })
        .eq('run_id', run.id)
        .eq('status', 'active');
      if (error) throw error;
      const { error: e2 } = await supabase
        .from('creator_outreach_runs')
        .update({ status: 'cancelled', completed_at: new Date().toISOString() })
        .eq('id', run.id);
      if (e2) throw e2;
      await logUpdate('creator_outreach_runs', run.id, { status: run.status }, { status: 'cancelled' });
    },
    onSuccess: () => {
      toast.success('Run cancelled. Emails already queued in the last few minutes may still go out.');
      queryClient.invalidateQueries({ queryKey: ['outreach-runs'] });
      queryClient.invalidateQueries({ queryKey: ['outreach-run-status', openRunId] });
      // Cancelling frees everyone in it to be enrolled again.
      queryClient.invalidateQueries({ queryKey: ['outreach-prior'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const selectedGroup = filteredBrands.find(b => b.brand_name === selectedBrand) ?? (brandsQuery.data ?? []).find(b => b.brand_name === selectedBrand);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Trybe Outreach</h1>
        <p className="text-muted-foreground">Pick a brand. Every creator who posted for it gets the three-email Trybe invite. {SEQUENCE_SUMMARY}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        {/* ── Brand picker ── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">1. Select a brand</CardTitle>
            <CardDescription>Brands from BMC Max / hybrid campaigns and Creator Roster hires.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="pl-8" placeholder="Search brands" value={brandSearch} onChange={e => setBrandSearch(e.target.value)} />
            </div>
            <div className="flex justify-between px-3 text-[10px] uppercase tracking-wide text-muted-foreground">
              <span>Brand</span>
              <span>Creators to email</span>
            </div>
            <div className="max-h-[520px] overflow-y-auto divide-y rounded-md border">
              {brandsQuery.isLoading && <div className="p-3 text-sm text-muted-foreground">Loading brands…</div>}
              {brandsQuery.error && <div className="p-3 text-sm text-destructive">Could not load brands: {(brandsQuery.error as Error).message}</div>}
              {filteredBrands.map(b => (
                <button
                  key={b.brand_name}
                  type="button"
                  onClick={() => { setSelectedBrand(b.brand_name); setExcluded(new Set()); setEmailEdits({}); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${selectedBrand === b.brand_name ? 'bg-muted font-semibold' : ''}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">{b.brand_name}</span>
                    <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">{b.creators} creators</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{b.sources.map(s => (s === 'hybrid' ? 'BMC Max' : 'Roster')).join(' + ')} · {b.videos} videos</div>
                </button>
              ))}
              {!brandsQuery.isLoading && !filteredBrands.length && <div className="p-3 text-sm text-muted-foreground">No brands match.</div>}
            </div>
          </CardContent>
        </Card>

        {/* ── Preview + run ── */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">2. Preview creators{selectedBrand ? ` for ${selectedBrand}` : ''}</CardTitle>
              <CardDescription>
                {selectedBrand
                  ? `${creatorsQuery.data?.length ?? 0} creators posted for ${selectedBrand}${selectedGroup ? ` (${selectedGroup.videos} videos)` : ''}. Untick anyone who should not get it. Fix an email inline if it looks wrong.`
                  : 'Select a brand on the left.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedBrand && creatorsQuery.isLoading && <div className="text-sm text-muted-foreground">Loading creators…</div>}
              {creatorsQuery.error && <div className="text-sm text-destructive">Could not load creators: {(creatorsQuery.error as Error).message}</div>}

              {/* What is actually going into the emails, before it goes out.
                  Rejected videos are excluded outright; unreviewed ones are
                  included, so the pending number is the one that is worth a
                  decision — approve them first and they go out as approved,
                  or send now and they go out anyway. */}
              {!!creatorsQuery.data?.length && (
                <div className="mb-3 space-y-2">
                  <div className="flex flex-wrap gap-x-4 gap-y-1 rounded-md border bg-muted/40 px-3 py-2 text-xs">
                    <span><strong className="tabular-nums">{videoTotals.inEmail}</strong> videos will be listed</span>
                    <span className="text-muted-foreground"><strong className="tabular-nums">{videoTotals.approved}</strong> approved</span>
                    {videoTotals.pending > 0 && (
                      <span className="text-muted-foreground"><strong className="tabular-nums">{videoTotals.pending}</strong> not reviewed yet</span>
                    )}
                    {videoTotals.rejected > 0 && (
                      <span className="text-muted-foreground"><strong className="tabular-nums">{videoTotals.rejected}</strong> rejected, left out</span>
                    )}
                  </div>
                  {inFlightCount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {inFlightCount} creator{inFlightCount === 1 ? '' : 's'}{' '}
                      {inFlightCount === 1 ? 'is' : 'are'} already part way through a sequence for this
                      brand and cannot be added again — they would get two sets of the same emails.
                      Let that run finish, or stop their enrollment in it first.
                    </p>
                  )}
                  {videoTotals.pending > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {videoTotals.pending} video{videoTotals.pending === 1 ? '' : 's'} across{' '}
                      {videoTotals.creatorsWithPending} creator{videoTotals.creatorsWithPending === 1 ? '' : 's'}{' '}
                      {videoTotals.pending === 1 ? 'has' : 'have'} not been reviewed. They will still be
                      included — review them first if you want the email to offer only approved work.
                    </p>
                  )}
                </div>
              )}

              {!!creatorsQuery.data?.length && (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Handle</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">In email</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {creatorsQuery.data.map(c => {
                        const addr = (emailEdits[c.email] ?? c.email).trim().toLowerCase();
                        const inFlight = !!priorQuery.data?.inFlight.has(addr);
                        const locked = c.unsubscribed || inFlight;
                        const off = locked || excluded.has(c.email);
                        const prior = priorQuery.data?.everSent.has(addr);
                        return (
                          <TableRow key={c.email} className={off ? 'opacity-50' : ''}>
                            <TableCell>
                              <Checkbox
                                checked={!off}
                                disabled={locked}
                                onCheckedChange={(v) => {
                                  const next = new Set(excluded);
                                  if (v) next.delete(c.email); else next.add(c.email);
                                  setExcluded(next);
                                }}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{c.handle ? `@${c.handle}` : (c.first_name ?? '—')}</TableCell>
                            <TableCell>
                              <Input
                                className="h-8 text-sm"
                                value={emailEdits[c.email] ?? c.email}
                                onChange={e => setEmailEdits({ ...emailEdits, [c.email]: e.target.value })}
                                disabled={locked}
                              />
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{c.source === 'hybrid' ? 'BMC Max' : c.source === 'roster' ? 'Roster' : 'Both'}</Badge>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              <div>{c.video_count}</div>
                              {(c.pending_count > 0 || c.rejected_count > 0) && (
                                <div
                                  className="text-[11px] font-normal text-muted-foreground"
                                  title={
                                    `${c.approved_count} approved, ${c.pending_count} not reviewed yet` +
                                    (c.rejected_count > 0 ? `, ${c.rejected_count} rejected and left out of the email` : '')
                                  }
                                >
                                  {[
                                    c.approved_count > 0 ? `${c.approved_count} ok` : null,
                                    c.pending_count  > 0 ? `${c.pending_count} pending` : null,
                                    c.rejected_count > 0 ? `${c.rejected_count} rejected` : null,
                                  ].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {c.unsubscribed
                                ? 'Unsubscribed'
                                : inFlight
                                  ? <span title="Already part way through a sequence for this brand. Let that run finish, or stop their enrollment in it first.">In an active run</span>
                                  : prior ? 'Sent before' : ''}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <EmailPreviewCard brand={selectedBrand} joinUrl={linkValid ? effectiveLink : null} />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">3. Add anyone else (optional)</CardTitle>
              <CardDescription>One per line. An email, or an email plus @handle. These get the same sequence for {selectedBrand ?? 'the selected brand'} without a video list.</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea rows={3} placeholder={'creator@example.com @theirhandle\nanother@example.com'} value={manualText} onChange={e => setManualText(e.target.value)} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">4. Run</CardTitle>
              <CardDescription>{SEQUENCE_SUMMARY}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span><strong>{recipients.length}</strong> will be emailed</span>
                {alreadySentCount > 0 && <span className="text-muted-foreground">({alreadySentCount} got this sequence in an earlier run)</span>}
              </div>
              <Button
                disabled={!selectedBrand || !recipients.length || runMutation.isPending}
                onClick={() => { setEditingLink(false); setLinkDraft(''); setConfirmOpen(true); }}
              >
                <Send className="mr-2 h-4 w-4" />
                {runMutation.isPending ? 'Starting…' : `Run for ${selectedBrand ?? 'brand'}`}
              </Button>

              {/* Last stop before the mail goes out. The link is the one thing
                  on this page nobody could previously verify, and it is the
                  one thing every recipient acts on. */}
              <Dialog open={confirmOpen} onOpenChange={o => { if (!runMutation.isPending) setConfirmOpen(o); }}>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Link2 className="h-4 w-4" />
                      Check the Trybe link
                    </DialogTitle>
                    <DialogDescription>
                      All <strong>{recipients.length}</strong> creator{recipients.length === 1 ? '' : 's'} for{' '}
                      <strong>{selectedBrand}</strong> get this link, in all three emails. Open it and
                      confirm it lands on the right program before starting the run.
                    </DialogDescription>
                  </DialogHeader>

                  {joinUrlQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">Loading the current link…</p>
                  ) : joinUrlQuery.isError ? (
                    <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        Could not read the current link from the worker ({(joinUrlQuery.error as Error).message}).
                        Type the link you want this run to use.
                      </span>
                    </div>
                  ) : null}

                  {editingLink ? (
                    <div className="space-y-2">
                      <Label htmlFor="trybe-link">Trybe program invite URL</Label>
                      <Input
                        id="trybe-link"
                        value={linkDraft}
                        onChange={e => setLinkDraft(e.target.value)}
                        placeholder="https://jointrybe.com/auth/invite/…"
                        className="font-mono text-xs"
                        autoFocus
                      />
                      {linkDraft.trim() && !linkValid && (
                        <p className="text-xs text-red-500">
                          Must be a full https:// URL, with no spaces, quotes or angle brackets.
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Used for this run only. It does not change the default for future runs.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="rounded-md border bg-muted/40 p-3">
                        <p className="break-all font-mono text-xs">{effectiveLink || '—'}</p>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground">
                          {joinUrlQuery.data?.source === 'env'
                            ? 'From the deployed OUTREACH_TRYBE_JOIN_URL setting'
                            : joinUrlQuery.data?.source === 'default'
                              ? "The worker's built-in default"
                              : ''}
                        </span>
                        {effectiveLink && (
                          <a
                            href={effectiveLink}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            Open to check
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  <DialogFooter className="gap-2 sm:gap-2">
                    {editingLink ? (
                      <>
                        <Button
                          variant="outline"
                          disabled={runMutation.isPending}
                          onClick={() => { setEditingLink(false); setLinkDraft(''); }}
                        >
                          Cancel
                        </Button>
                        <Button
                          disabled={!linkValid || runMutation.isPending}
                          onClick={() => setEditingLink(false)}
                        >
                          Use this link
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          disabled={runMutation.isPending}
                          onClick={() => { setLinkDraft(effectiveLink); setEditingLink(true); }}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Not right — update it
                        </Button>
                        <Button
                          disabled={!linkValid || runMutation.isPending}
                          onClick={() => runMutation.mutate(effectiveLink)}
                        >
                          <Send className="mr-2 h-4 w-4" />
                          {runMutation.isPending
                            ? 'Starting…'
                            : `Link is correct — email ${recipients.length}`}
                        </Button>
                      </>
                    )}
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Runs ── */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Runs</CardTitle>
            <CardDescription>Click a run to see per-creator progress. Refreshes every minute.</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { queryClient.invalidateQueries({ queryKey: ['outreach-runs'] }); queryClient.invalidateQueries({ queryKey: ['outreach-run-status'] }); }}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {runsQuery.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {!runsQuery.isLoading && !runsQuery.data?.length && <div className="text-sm text-muted-foreground">No runs yet.</div>}
          {!!runsQuery.data?.length && (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Brand</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Creators</TableHead>
                    <TableHead className="text-right">Joined Trybe</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runsQuery.data.map(r => (
                    <TableRow key={r.id} className={`cursor-pointer ${openRunId === r.id ? 'bg-muted' : ''}`} onClick={() => setOpenRunId(openRunId === r.id ? null : r.id)}>
                      <TableCell className="font-medium">{r.brand_name}</TableCell>
                      <TableCell>
                        <Badge variant={runStatusVariant[r.status] ?? 'outline'}>{r.status}</Badge>
                        {r.error && <span className="ml-2 text-xs text-destructive">{r.error}</span>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{r.recipient_count ?? '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">{joinedCounts.data?.[r.id] ?? '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDistanceToNow(new Date(r.started_at ?? r.created_at), { addSuffix: true })}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.created_by ?? '—'}</TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        {(r.status === 'queued' || r.status === 'active') && (
                          <Button variant="ghost" size="sm" onClick={() => { if (window.confirm(`Cancel the ${r.brand_name} run? Creators who have not received all three emails stop here.`)) cancelRun.mutate(r); }}>
                            <StopCircle className="mr-1 h-4 w-4" /> Cancel
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {openRunId && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-sm">Creators in this run</Label>
                <div className="flex flex-wrap gap-1">
                  {RUN_FILTERS.map(f => {
                    const count = f.key === 'all' ? runSummary.creators
                      : f.key === 'attention' ? runSummary.attention
                      : f.key === 'unsubscribed' ? runSummary.unsubscribed
                      : runSummary.joined;
                    return (
                      <Button
                        key={f.key}
                        variant={runFilter === f.key ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setRunFilter(f.key)}
                      >
                        {f.label} <span className="ml-1 tabular-nums text-muted-foreground">{count}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Two rows of counts: where the creators got to, and what the
                  individual sends did. A run can be 'active' with every email
                  failed, so neither number tells the story on its own. */}
              {!!runStatusQuery.data?.length && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 rounded-md border bg-muted/40 px-3 py-2 text-xs">
                  <span><strong className="tabular-nums">{runSummary.active}</strong> active</span>
                  <span><strong className="tabular-nums">{runSummary.done}</strong> done</span>
                  {runSummary.stopped > 0 && <span><strong className="tabular-nums">{runSummary.stopped}</strong> stopped</span>}
                  {runSummary.unsubscribed > 0 && <span><strong className="tabular-nums">{runSummary.unsubscribed}</strong> unsubscribed</span>}
                  <span className="text-muted-foreground">·</span>
                  <span><strong className="tabular-nums">{runSummary.emailsSent}</strong> emails sent</span>
                  {runSummary.emailsQueued > 0 && <span className="text-muted-foreground"><strong className="tabular-nums">{runSummary.emailsQueued}</strong> waiting to send</span>}
                  {runSummary.emailsFailed > 0 && <span className="text-destructive"><strong className="tabular-nums">{runSummary.emailsFailed}</strong> failed</span>}
                  {runSummary.bounced > 0 && <span className="text-destructive"><strong className="tabular-nums">{runSummary.bounced}</strong> bounced</span>}
                  <span className="text-muted-foreground">·</span>
                  <span><strong className="tabular-nums">{runSummary.opened}</strong> opened something</span>
                  <span><strong className="tabular-nums">{runSummary.joined}</strong> joined Trybe</span>
                </div>
              )}

              {runStatusQuery.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
              {!!runStatusQuery.data && (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Creator</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Email 1</TableHead>
                        <TableHead>Email 2</TableHead>
                        <TableHead>Email 3</TableHead>
                        <TableHead>Trybe</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!visibleRunRows.length && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-sm text-muted-foreground">
                            No creators match this filter.
                          </TableCell>
                        </TableRow>
                      )}
                      {visibleRunRows.map(row => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <div className="font-medium">{row.handle ? `@${row.handle}` : row.email}</div>
                            <div className="text-xs text-muted-foreground">{row.email}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={row.status === 'active' ? 'default' : row.status === 'done' ? 'secondary' : 'outline'}>{row.status}</Badge>
                            {row.bounced && <Badge variant="destructive" className="ml-1">bounced</Badge>}
                            {row.stop_reason && <div className="text-xs text-muted-foreground">{row.stop_reason}</div>}
                            {row.last_error && <div className="text-xs text-destructive">{row.last_error}</div>}
                          </TableCell>
                          <StepCell sentAt={row.step_1_sent_at} queueStatus={row.step_1_queue_status} opened={row.step_1_opened} error={row.step_1_queue_error} attempts={row.step_1_attempts} />
                          <StepCell sentAt={row.step_2_sent_at} queueStatus={row.step_2_queue_status} opened={row.step_2_opened} error={row.step_2_queue_error} attempts={row.step_2_attempts} />
                          <StepCell sentAt={row.step_3_sent_at} queueStatus={row.step_3_queue_status} opened={row.step_3_opened} error={row.step_3_queue_error} attempts={row.step_3_attempts} />
                          <TableCell className="text-xs whitespace-nowrap">
                            {row.trybe_assigned_at ? (
                              <Badge variant="secondary">assigned {format(new Date(row.trybe_assigned_at), 'MMM d')}</Badge>
                            ) : row.trybe_joined_at ? (
                              <>
                                <Badge variant="outline">joined {format(new Date(row.trybe_joined_at), 'MMM d')}</Badge>
                                <Button variant="ghost" size="sm" className="ml-1 h-6 px-2 text-xs" onClick={() => markTrybe.mutate({ row, field: 'trybe_assigned_at' })}>Mark assigned</Button>
                              </>
                            ) : (
                              <>
                                <span className="text-muted-foreground">—</span>
                                <Button variant="ghost" size="sm" className="ml-1 h-6 px-2 text-xs" onClick={() => markTrybe.mutate({ row, field: 'trybe_joined_at' })}>Mark joined</Button>
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            {row.status === 'active' && (
                              <Button variant="ghost" size="sm" onClick={() => stopEnrollment.mutate(row)}>Stop</Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Read-only preview of the three emails, rendered by the worker's real
// templates (GET /api/admin/outreach/preview) for the selected brand. Copy
// lives in Brand-Dashboard/worker/src/outreach.ts and is not editable here.
const PREVIEW_STEPS: { step: 1 | 2 | 3; label: string }[] = [
  { step: 1, label: 'Email 1 · Day 0' },
  { step: 2, label: 'Email 2 · Day 3' },
  { step: 3, label: 'Email 3 · Day 7' },
];

function EmailPreviewCard({ brand, joinUrl }: {
  brand: string | null;
  /** The link the next run will actually send — whatever the confirm dialog
   *  resolved to, including an admin override. Omitted (null) before the
   *  dialog has been opened, in which case the worker fills in its own
   *  default, which is what that run would get anyway. Showing the email with
   *  a different link than the run will send is the exact confusion this
   *  whole feature exists to remove. */
  joinUrl: string | null;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const previewBrand = brand ?? 'Above';
  const previewQuery = useQuery({
    // joinUrl is in the key so editing the link re-renders the preview rather
    // than serving the previous link's cached HTML.
    queryKey: ['outreach-email-preview', previewBrand, step, joinUrl],
    queryFn: async () => {
      const path = `/api/admin/outreach/preview?step=${step}&brand=${encodeURIComponent(previewBrand)}&format=json`
        + (joinUrl ? `&join_url=${encodeURIComponent(joinUrl)}` : '');
      // Secret + admin JWT, like every other /api/admin/* call.
      return workerAuthedFetch<{ subject: string; html: string }>(path, { method: 'GET' });
    },
    staleTime: 5 * 60 * 1000,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">What they will receive</CardTitle>
        <CardDescription>
          Read-only preview of the three emails, filled in for {previewBrand}. The copy is fixed; to change it, ask Mike.
          {joinUrl
            ? ' The "Join the program" button points at the link this run will send.'
            : ' The "Join the program" button points at the current default link.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {PREVIEW_STEPS.map(s => (
            <Button key={s.step} size="sm" variant={s.step === step ? 'default' : 'outline'} onClick={() => setStep(s.step)}>{s.label}</Button>
          ))}
        </div>
        {previewQuery.isLoading && <div className="text-sm text-muted-foreground">Rendering…</div>}
        {previewQuery.error && <div className="text-sm text-destructive">Could not load the preview: {(previewQuery.error as Error).message}</div>}
        {previewQuery.data && (
          <>
            <div className="text-sm">Subject: <strong>{previewQuery.data.subject}</strong></div>
            <iframe title={`Email ${step} preview`} srcDoc={previewQuery.data.html} sandbox="" className="h-[560px] w-full rounded-md border bg-white" />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function StepCell({ sentAt, queueStatus, opened, error, attempts }: {
  sentAt: string | null;
  queueStatus: string | null;
  opened: boolean;
  error?: string | null;
  attempts?: number | null;
}) {
  if (!sentAt) return <TableCell className="text-xs text-muted-foreground">—</TableCell>;
  const label = queueStatus === 'sent' ? 'sent' : queueStatus === 'failed' ? 'failed' : queueStatus === 'skipped' ? 'skipped' : 'queued';
  const failed = label === 'failed';
  return (
    <TableCell className="text-xs align-top">
      <div className={failed ? 'text-destructive' : ''}>{label}{opened ? ' · opened' : ''}</div>
      <div className="text-muted-foreground">{formatDistanceToNow(new Date(sentAt), { addSuffix: true })}</div>
      {/* The drain retries a transient failure up to 3 times on its own, so the
          attempt count is what says whether this still has a chance of going
          out or has been given up on. */}
      {failed && (attempts ?? 0) > 0 && (
        <div className="text-muted-foreground">attempt {attempts}/3</div>
      )}
      {failed && error && (
        <div className="mt-0.5 max-w-[220px] truncate text-destructive/80" title={error}>{error}</div>
      )}
    </TableCell>
  );
}
